/**
 * IMTask API client (adapted for ARCHI-HUB integration).
 *
 * - Base URL: `{REACT_APP_BACKEND_URL}/api/imtask`
 * - Auth: usa lo stesso token JWT di ARCHI-HUB (`localStorage.ah_token`) + cookie
 *   (SSO, nessun /auth locale).
 * - Endpoints /auth/* rimossi (delegati ad ARCHI-HUB).
 * - SSE streaming per la chat AI (`/ai/chat`).
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api/imtask`;

const client = axios.create({ baseURL: API, withCredentials: true });

client.interceptors.request.use((config) => {
  const t = localStorage.getItem("ah_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// AUTH — delegato ad ARCHI-HUB (login/register in /login /register).
// Manteniamo authMe come no-op che ritorna l'utente ARCHI-HUB (verrà iniettato da auth.jsx shim).
export const authMe = () => {
  const u = window.__ARCHIHUB_USER__;
  if (u) return Promise.resolve(u);
  return Promise.reject(new Error("Non autenticato"));
};
export const authLogout = () => Promise.resolve({ ok: true });

// Users (per sharing task tra admin)
export const listUsers = () => client.get("/users").then((r) => r.data);

// Notifications
export const listNotifications = () => client.get("/notifications").then((r) => r.data);
export const readAllNotifications = () => client.post("/notifications/read-all").then((r) => r.data);
export const deleteNotification = (id) => client.delete(`/notifications/${id}`).then((r) => r.data);

// Tasks
export const listTasks = () => client.get("/tasks").then((r) => r.data);
export const createTask = (data) => client.post("/tasks", data).then((r) => r.data);
export const updateTask = (id, data) => client.put(`/tasks/${id}`, data).then((r) => r.data);
export const deleteTask = (id) => client.delete(`/tasks/${id}`).then((r) => r.data);

// Post-its
export const listPostIts = () => client.get("/postits").then((r) => r.data);
export const createPostIt = (data) => client.post("/postits", data).then((r) => r.data);
export const updatePostIt = (id, data) => client.put(`/postits/${id}`, data).then((r) => r.data);
export const deletePostIt = (id) => client.delete(`/postits/${id}`).then((r) => r.data);

// Recurring
export const listRecurring = () => client.get("/recurring").then((r) => r.data);
export const createRecurring = (data) => client.post("/recurring", data).then((r) => r.data);
export const updateRecurring = (id, data) => client.put(`/recurring/${id}`, data).then((r) => r.data);
export const deleteRecurring = (id) => client.delete(`/recurring/${id}`).then((r) => r.data);

// Suppliers (leggeri, per rubrica CRM)
export const listSuppliers = () => client.get("/suppliers").then((r) => r.data);
export const saveSupplier = (data) => client.post("/suppliers", data).then((r) => r.data);

// Fornitori ARCHI-HUB (piattaforma listini) — includono gli sconti acquisto
// riservati (purchase_discount_chain) usati per il Calcolo Prezzo.
export const listArchiSuppliers = () =>
  axios
    .get(`${BACKEND_URL}/api/suppliers`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${localStorage.getItem("ah_token") || ""}` },
    })
    .then((r) => r.data);
export const deleteSupplier = (id) => client.delete(`/suppliers/${id}`).then((r) => r.data);

// Settings
export const getSettings = () => client.get("/settings").then((r) => r.data);
export const updateSettings = (data) => client.put("/settings", data).then((r) => r.data);

// Backup
export const exportBackup = () => client.get("/backup").then((r) => r.data);
export const importBackup = (data) => client.post("/backup/import", data).then((r) => r.data);

// Clients (Rubrica)
export const listClients = () => client.get("/clients").then((r) => r.data);
export const clientDetail = (name) =>
  client.get(`/clients/${encodeURIComponent(name)}`).then((r) => r.data);

// AI
export const aiDailySummary = () => client.post("/ai/daily-summary", {}).then((r) => r.data);
export const aiGenerateEmail = (task_id, tone) =>
  client.post("/ai/generate-email", { task_id, tone }).then((r) => r.data);
export const aiRecallSuggestion = (task_id) =>
  client.post("/ai/recall-suggestion", { task_id }).then((r) => r.data);
export const aiAutoDescription = (short, category, clientName) =>
  client.post("/ai/auto-description", { short, category, client: clientName }).then((r) => r.data);
export const aiChatHistory = (session_id = "default") =>
  client
    .get("/ai/chat/history", { params: { session_id: `user-${window.__CURRENT_USER_ID__}-${session_id}` } })
    .then((r) => r.data);
export const aiChatClear = (session_id = "default") =>
  client
    .delete("/ai/chat/history", { params: { session_id: `user-${window.__CURRENT_USER_ID__}-${session_id}` } })
    .then((r) => r.data);

// Streaming chat SSE — usa fetch API con Bearer token da ah_token
export async function aiChatStream(message, session_id, onDelta, onDone, onError) {
  try {
    const token = localStorage.getItem("ah_token");
    const resp = await fetch(`${API}/ai/chat`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ message, session_id }),
    });
    if (!resp.ok || !resp.body) {
      onError && onError(new Error(`Errore chat (${resp.status})`));
      return;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const json = JSON.parse(line.slice(6));
          if (json.delta) onDelta && onDelta(json.delta);
          if (json.done) onDone && onDone();
          if (json.error) onError && onError(new Error(json.error));
        } catch (e) {
          /* skip */
        }
      }
    }
    onDone && onDone();
  } catch (e) {
    onError && onError(e);
  }
}

export default client;
